import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { setElementsScoreSettings, useElementsScoreSettings } from 'reducers/reviewCreationSlice';

import { GoalThinOutline } from 'assets/assetsV1/iconComponents';

import { StringModifier, modifyString } from 'constants/copyHelpers';

import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { RadioSelect } from 'uiToolkitV1/RadioSelect';
import { autoComputeRatingInitialSetting } from 'uiToolkitV1/ScoreSettingTable/ScoreSettingTable';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { ScoreSettings } from './ScoreSetting';
import RATING_COMPUTATION_TYPE from './constants';
import useGoalAspect, { GOAL_ASPECTS } from './hooks/useGoalAspect';

const GoalSettings = ({ feedbackFormId }) => {
  const dispatch = useDispatch();
  const { isGoalRatingAutoComputed, goalRatingComputationType, isIncludeCollaboratorGoals } = useElementsScoreSettings({
    feedbackFormId,
  });

  const onChangeHandler = (newValue, propertyName) => {
    dispatch(
      setElementsScoreSettings({
        feedbackFormId,
        settings: { [propertyName]: newValue },
      })
    );
    if (propertyName === 'isGoalRatingAutoComputed') {
      if (!newValue) {
        dispatch(
          setElementsScoreSettings({
            feedbackFormId,
            settings: { autoComputeGoalRatingSettings: [], isGoalSectionEditable: true },
          })
        );
      } else {
        dispatch(
          setElementsScoreSettings({
            feedbackFormId,
            settings: { autoComputeGoalRatingSettings: autoComputeRatingInitialSetting },
          })
        );
      }
    }
  };

  const { index: selectedIndex } = useGoalAspect({ feedbackFormId });
  const { objectivesName, keyResultsName, moduleName } = useOrganisationSettings();

  return (
    <ExpandablePaper>
      <ExpandablePaperHeader
        title={`${modifyString(objectivesName, StringModifier.PLURAL_TITLE_CASE)} Review Settings`}
        leftIconComponent={<GoalThinOutline boxSize="24px" color="icon.g2" />}
      />
      <ExpandablePaperBody>
        <Box p="10px 8px">
          <Flex alignItems="center">
            <TextBodyMedium mr={2} mb="2px">
              Use configured weightage for goals or equal weightage:
            </TextBodyMedium>
            <TooltipInfoV1
              title={`Configured weightage is the weights set for employees using 'Manage weightage'. Equal weightage distributes 100% equally across all ${modifyString(
                objectivesName,
                StringModifier.PLURAL_SENTENCE_CASE
              )} picked in the review`}
            />
          </Flex>

          <RadioSelect
            options={[{ name: 'Yes, configured' }, { name: 'No, use equal weightage' }]}
            selectedIndex={goalRatingComputationType === RATING_COMPUTATION_TYPE.WEIGHTED_AVERAGE ? 0 : 1}
            onChange={(index) =>
              onChangeHandler(
                index === 0 ? RATING_COMPUTATION_TYPE.WEIGHTED_AVERAGE : RATING_COMPUTATION_TYPE.AVERAGE,
                'goalRatingComputationType'
              )
            }
          />
        </Box>

        <Box p="10px 8px" mt={3}>
          <Flex alignItems="center">
            <TextBodyMedium mr={2} mb="2px">
              Auto-calculate the {modifyString(objectivesName, StringModifier.PLURAL_TITLE_CASE)} rating based on
              progress made on them?
            </TextBodyMedium>
            <TooltipInfoV1
              title={`Auto-calculate will use the below rating logic to calculate the ${modifyString(
                objectivesName,
                StringModifier.PLURAL_TITLE_CASE
              )} rating`}
            />
          </Flex>

          <RadioSelect
            options={[{ name: 'Yes' }, { name: 'No' }]}
            selectedIndex={isGoalRatingAutoComputed ? 0 : 1}
            onChange={(index) => onChangeHandler(index === 0, 'isGoalRatingAutoComputed')}
          />
        </Box>

        {/* -------------------------- */}
        <ScoreSettings feedbackFormId={feedbackFormId} />

        <Box p="10px 8px" mt={3}>
          <Flex alignItems="center">
            <TextBodyMedium mr={2} mb="2px">
              Which aspects should the {modifyString(objectivesName, StringModifier.TITLE_CASE)} questions be evaluated
              on?
            </TextBodyMedium>
            <TooltipInfoV1
              title={`${modifyString(objectivesName, StringModifier.PLURAL_TITLE_CASE)} and ${modifyString(
                keyResultsName,
                StringModifier.PLURAL_TITLE_CASE
              )} compliments the equal weightage distribution choice. Else the sum of both will be considered if configured weightage is chosen`}
            />
          </Flex>
          <TextLabel mt={2} color="text.g2">
            Note: Self & Manager will see all {modifyString(objectivesName, StringModifier.PLURAL_TITLE_CASE)}/
            {modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)}. Peer, DLM, Upward reviewers will see based
            on viewing rights set in Goal Preferences & on individual &apos;
            {modifyString(objectivesName, StringModifier.TITLE_CASE)}/
            {modifyString(keyResultsName, StringModifier.TITLE_CASE)}&apos; results
          </TextLabel>

          <RadioSelect
            options={GOAL_ASPECTS}
            selectedIndex={selectedIndex}
            onChange={(index) => {
              onChangeHandler(index === 0 || index === 2, 'isIncludeGoals');
              onChangeHandler(index === 1 || index === 2, 'isIncludeObjectives');
            }}
          />
        </Box>

        <Box p="10px 8px" mt={3}>
          <Flex alignItems="center">
            <TextBodyMedium mr={2} mb="2px">
              {modifyString(moduleName, StringModifier.TITLE_CASE)} ownership type for evaluation?
            </TextBodyMedium>
          </Flex>

          <RadioSelect
            options={[{ name: 'Owner Only' }, { name: 'Owner or Collaborator' }]}
            selectedIndex={isIncludeCollaboratorGoals ? 1 : 0}
            onChange={(index) => onChangeHandler(index === 1, 'isIncludeCollaboratorGoals')}
          />
        </Box>
      </ExpandablePaperBody>
    </ExpandablePaper>
  );
};

export { GoalSettings };
